import { Capacitor } from "@capacitor/core";
import { initConversationEngine } from "../../native/conversationEngine";
import { configureNativeApiOrigin, getNativeApiOrigin } from "../../native/nativeApiOrigin";
import StytchAuth, {
    addLoginRequestedListener,
    nativeTokensFrom,
    type NativeSession,
} from "../../native/nativeAuth";
import {
    getClientIdentity,
    reportConnectivityObservation,
    reportUpdateRequired,
} from "../../native/nativeNav";
import bridgeManifest from "../../native/bridgeManifest.json";
import { nativeNavAdmission } from "../../native/nativeNavAdmission";
import { configureNativeViewport } from "../../native/nativeViewport";
import type { AuthSession, Platform, TenantIdentity } from "../Platform";
import { amplitudeApiKeyForOrigin } from "../amplitudeApiKey";
import { createBrowserCapabilities } from "../browserCapabilities";
import { createNativeStytchClient } from "./createNativeStytchClient";
import { createBrowserPrefs } from "../createBrowserPrefs";
import { canonicalScopeOrgSlug, embedOrganizationSlug } from "../tenantIdentity";
import { installWebErrorReporting } from "./webErrorReporting";

function sessionTokens(session: NativeSession | null): AuthSession | null {
    return session ? nativeTokensFrom(session) : null;
}

export async function createIosPlatform(): Promise<Platform> {
    if (!Capacitor.isNativePlatform()) {
        throw new Error("iOS platform requires a Capacitor native host");
    }

    // First, so an error anywhere in the page's own boot is reported too.
    installWebErrorReporting();
    configureNativeApiOrigin();
    configureNativeViewport();
    initConversationEngine();

    const capabilities = createBrowserCapabilities();
    const tenant: TenantIdentity = {
        orgSlug() {
            const scoped = canonicalScopeOrgSlug();
            return scoped !== undefined ? scoped : embedOrganizationSlug();
        },
    };
    const prefs = createBrowserPrefs(tenant, getNativeApiOrigin);
    // The shell's own build (`ios <version> (<build>) g<sha>`), named on the page's requests too.
    const clientIdentity = await getClientIdentity();
    const apiOrigin = getNativeApiOrigin();
    return {
        kind: "ios",
        // The shell runs on its own bundled paths, never a project URL.
        projectAdmission: {
            owner: "app",
            supportsCanonicalSelection: true,
            supportsUrlRouting: false,
            native: nativeNavAdmission,
        },
        bootstrap: {
            apiOrigin,
            queryNetworkMode: "offlineFirst",
        },
        open: () => "fallback",
        tenant,
        auth: {
            withCredentials: false,
            credentials: "omit",
            stytchClient: createNativeStytchClient(),
            async getSession() {
                return sessionTokens((await StytchAuth.getSession()).session);
            },
            async refresh() {
                return sessionTokens((await StytchAuth.refresh()).session);
            },
            async login() {
                await StytchAuth.login();
            },
            logout(options) {
                return StytchAuth.logout(options);
            },
            async onLoginRequested(callback) {
                const handle = await addLoginRequestedListener(callback);
                return () => {
                    void handle.remove();
                };
            },
        },
        ...capabilities,
        // The page is served from `capacitor://localhost` whatever backend it talks to: the
        // backend picks the project, as it does for the shell's own events (`NativeAnalytics`).
        amplitudeApiKey: amplitudeApiKeyForOrigin(apiOrigin),
        // The shell counts every screen's active time, its web screens included.
        activeTimeCounter: "native",
        // The native shell renders PDFs with PDFKit; the page never loads PDFium.
        pdfRenderer: "native",
        // Native Marvin renders what the bridge manifest declares; its Swift tests read it too.
        marvinRenders: bridgeManifest.marvinRenders,
        // Marvin is the shell's own native screens; the web views mount none.
        marvinRenderer: "native",
        // My collections is web and desktop only in v1; the shell has no screens for it.
        showsCollections: false,
        offersMobileApp: false,
        voiceInput: false,
        clientIdentity,
        // One Update Primepoint screen: the page hands a floored answer to native.
        appUpdate: { required: reportUpdateRequired },
        // The shell owns the WebView's URL; the app routes in memory. It has no tabs: a
        // route asked for in a new tab opens in this view.
        navigation: { router: "memory", tabs: "none" },
        prefs,
        // iOS keeps no local mirror; its offline work is the transport's replay cache
        // (on for any host with an offline capability) and telling the native shell
        // what the transport observed.
        offline: {
            connectivity: { observe: reportConnectivityObservation },
        },
    };
}
